import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import { BASE_URL } from "../../config";
import { ArrowLeftIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { useTable, usePagination } from "react-table";

const CptHistory = () => {
  const navigate = useNavigate();
  const { cptCode } = useParams();
  const [data, setData] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchHistory = async (pageIndex, pageSize) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/cpt/${cptCode}/history`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
        },
        params: {
          limit_offset: pageIndex * pageSize,
          limit: pageSize,
        },
      });
      setData(response.data.data.history);
      setStatusHistory(response.data.data.status_history || []);
      setTotalCount(response.data.total_count);
    } catch (error) {
      toast.error("Failed to fetch CPT history.", { autoClose: 3000 });
      setTimeout(() => navigate("/cpt"), 2000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory(currentPage - 1, itemsPerPage);
  }, [cptCode, currentPage, itemsPerPage]);

  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    return data.filter((log) =>
      [
        log.type,
        log.action,
        log.username,
        `${log.first_name || ''} ${log.last_name || ''}`,
        log.type === 'master'
          ? Object.values(log.details).join(' ')
          : `${log.details.provider_code} ${log.details.provider_cpt_code} ${log.details.cpt_rate} ${log.details.status}`,
      ]
        .join(' ')
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  }, [data, searchQuery]);

  const filteredStatusHistory = useMemo(() => {
    if (!searchQuery) return statusHistory;
    return statusHistory.filter((log) =>
      [
        log.type,
        log.action,
        log.username,
        `${log.first_name || ''} ${log.last_name || ''}`,
        `${log.details.prev_active_flag} ${log.details.new_active_flag} ${log.details.remarks || ''}`,
      ]
        .join(' ')
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  }, [statusHistory, searchQuery]);

  const columns = useMemo(
    () => [
      {
        Header: "Type",
        accessor: "type",
        Cell: ({ value }) => <span className="font-medium">{value === 'master' ? 'CPT Master' : 'CPT Rates'}</span>,
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Details",
        accessor: "details",
        Cell: ({ value, row }) => (
          <span className="text-gray-600">
            {row.original.type === 'master'
              ? `Description: ${value.cpt_desc}, Category: ${value.cpt_categ_code || 'N/A'}, Default Price: ₱${parseFloat(value.default_price).toLocaleString()}, Active: ${value.active_flag}`
              : `Provider: ${value.provider_code}, CPT Code: ${value.provider_cpt_code}, Rate: ₱${parseFloat(value.cpt_rate).toLocaleString()}, Status: ${value.status}`}
          </span>
        ),
      },
      {
        Header: "User",
        accessor: "username",
        Cell: ({ row }) => (
          <span>{`${row.original.first_name || ''} ${row.original.last_name || ''} (${row.original.username})`}</span>
        ),
      },
      {
        Header: "Timestamp",
        accessor: "action_timestamp",
        Cell: ({ value }) => (
          <span>{new Date(value).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}</span>
        ),
      },
    ],
    []
  );

  const statusColumns = useMemo(
    () => [
      {
        Header: "Type",
        accessor: "type",
        Cell: () => <span className="font-medium text-blue-600">Status Change</span>,
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: ({ value }) => <span>{value}</span>,
      },
      {
        Header: "Details",
        accessor: "details",
        Cell: ({ value }) => (
          <div className="text-gray-600">
            <span>Previous Status: {value.prev_active_flag === '1' ? 'Active' : value.prev_active_flag === '2' ? 'Terminated/Cancelled' : 'Suspended'}</span><br />
            <span>New Status: {value.new_active_flag === '1' ? 'Active' : value.new_active_flag === '2' ? 'Terminated/Cancelled' : 'Suspended'}</span><br />
            {value.remarks && (
              <span className="group relative">
                Remarks: {value.remarks.length > 50 ? `${value.remarks.substring(0, 50)}...` : value.remarks}
                {value.remarks.length > 50 && (
                  <span className="absolute z-10 invisible group-hover:visible bg-gray-800 text-white text-xs rounded py-1 px-2 -top-8 left-0 w-64">
                    {value.remarks}
                  </span>
                )}
              </span>
            )}
          </div>
        ),
      },
      {
        Header: "User",
        accessor: "username",
        Cell: ({ row }) => (
          <span>{`${row.original.first_name || ''} ${row.original.last_name || ''} (${row.original.username})`}</span>
        ),
      },
      {
        Header: "Timestamp",
        accessor: "action_timestamp",
        Cell: ({ value }) => (
          <span>{new Date(value).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}</span>
        ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageIndex: 0, pageSize: itemsPerPage },
      manualPagination: true,
      pageCount: Math.ceil(totalCount / itemsPerPage),
    },
    usePagination
  );

  const {
    getTableProps: getStatusTableProps,
    getTableBodyProps: getStatusTableBodyProps,
    headerGroups: statusHeaderGroups,
    rows: statusRows,
    prepareRow: prepareStatusRow,
  } = useTable({
    columns: statusColumns,
    data: filteredStatusHistory,
  });

  useEffect(() => {
    setCurrentPage(pageIndex + 1);
    fetchHistory(pageIndex, pageSize);
  }, [pageIndex, pageSize]);

  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    const startPage = Math.max(1, pageIndex + 1 - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(
      Math.ceil(totalCount / pageSize),
      startPage + maxPagesToShow - 1
    );
    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-2 px-2 sm:px-6 lg:px-8">
      <ToastContainer position="top-right" autoClose={3000} />
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-1xl mx-auto"
      >
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-gray-900">
            Trail Log: CPT Code History: {cptCode}
          </h1>
          <button
            type="button"
            onClick={() => navigate("/cpt")}
            className="inline-flex items-center justify-center gap-2 self-start rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 sm:self-auto"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>Back to CPT Rates</span>
          </button>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="mb-4 relative">
            <input
              type="text"
              placeholder="Search history by type, action, user, or details..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                gotoPage(0);
              }}
              className="pl-10 pr-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm w-full shadow-sm"
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">CTP Master & Rates History</h2>
          <div className="overflow-x-auto border border-gray-200 rounded-md">
            <table
              {...getTableProps()}
              className="min-w-full divide-y divide-gray-200"
            >
              <thead className="bg-gray-100">
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps()}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody
                {...getTableBodyProps()}
                className="bg-white divide-y divide-gray-200"
              >
                {loading ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-3 text-center text-sm text-gray-500"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : page.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-3 text-center text-sm text-gray-500"
                    >
                      {searchQuery ? "No matching history found." : "No history found."}
                    </td>
                  </tr>
                ) : (
                  page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} className="hover:bg-gray-50">
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            className="px-4 py-3 whitespace-nowrap text-sm text-gray-900"
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-3 px-4 py-2">
            <div className="text-sm text-gray-600">
              Showing {pageIndex * pageSize + 1} to{" "}
              {Math.min((pageIndex + 1) * pageSize, totalCount)} of{" "}
              {totalCount.toLocaleString()} entries
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
                className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm ${
                  !canPreviousPage
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <ChevronDoubleLeftIcon className="h-5 w-5" />
                <span>First</span>
              </button>
              <button
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm ${
                  !canPreviousPage
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <ChevronLeftIcon className="h-5 w-5" />
                <span>Previous</span>
              </button>
              {getPageNumbers().map((page) => (
                <button
                  key={page}
                  onClick={() => gotoPage(page - 1)}
                  className={`px-3 py-1 rounded-md text-sm ${
                    pageIndex + 1 === page
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => nextPage()}
                disabled={!canNextPage}
                className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm ${
                  !canNextPage
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <span>Next</span>
                <ChevronRightIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => gotoPage(Math.ceil(totalCount / pageSize) - 1)}
                disabled={!canNextPage}
                className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm ${
                  !canNextPage
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <span>Last</span>
                <ChevronDoubleRightIcon className="h-5 w-5" />
              </button>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  gotoPage(0);
                }}
                className="border border-gray-200 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size} per page
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Status History Section */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Status Change History</h2>
          <div className="overflow-x-auto border border-gray-200 rounded-md">
            <table
              {...getStatusTableProps()}
              className="min-w-full divide-y divide-gray-200"
            >
              <thead className="bg-gray-100">
                {statusHeaderGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps()}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody
                {...getStatusTableBodyProps()}
                className="bg-white divide-y divide-gray-200"
              >
                {loading ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-3 text-center text-sm text-gray-500"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : statusRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-3 text-center text-sm text-gray-500"
                    >
                      {searchQuery ? "No matching status history found." : "No status history found."}
                    </td>
                  </tr>
                ) : (
                  statusRows.map((row) => {
                    prepareStatusRow(row);
                    return (
                      <tr {...row.getRowProps()} className="hover:bg-gray-50">
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            className="px-4 py-3 whitespace-nowrap text-sm text-gray-900"
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CptHistory;
